export const IMAGE_LIMITS = {
  maxAttachments: 10,
  maxOriginalBytes: 25_000_000,
  maxStoredBytes: 5_000_000,
  maxDimension: 1920,
} as const

export interface CompressedImage {
  file: File
  originalName: string
  originalSize: number
  width: number
  height: number
}

const acceptedImageTypes = new Set(['image/png', 'image/jpeg', 'image/webp'])

export function formatBytes(value: number) {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function webpName(name: string) {
  const base = name.replace(/\.[^.]+$/, '').trim() || 'clipboard-image'
  return `${base}.webp`
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('這個瀏覽器無法產生 WebP 圖片。')),
      'image/webp',
      quality,
    )
  })
}

export async function compressImageToWebp(source: File): Promise<CompressedImage> {
  if (!acceptedImageTypes.has(source.type)) throw new Error('只支援 PNG、JPG、JPEG 或 WebP 圖片。')
  if (source.size <= 0 || source.size > IMAGE_LIMITS.maxOriginalBytes) throw new Error('原始圖片必須小於 25 MB。')

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(source)
  } catch {
    throw new Error('無法讀取這張圖片，請確認檔案沒有損壞。')
  }

  try {
    const scale = Math.min(1, IMAGE_LIMITS.maxDimension / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d', { alpha: true })
    if (!context) throw new Error('瀏覽器無法處理圖片。')
    context.drawImage(bitmap, 0, 0, width, height)

    let output: Blob | null = null
    for (const quality of [0.85, 0.76, 0.66, 0.56]) {
      output = await canvasBlob(canvas, quality)
      if (output.size <= IMAGE_LIMITS.maxStoredBytes) break
    }
    if (!output || output.type !== 'image/webp') throw new Error('圖片轉換成 WebP 失敗。')
    if (output.size > IMAGE_LIMITS.maxStoredBytes) throw new Error('壓縮後仍超過 5 MB，請先縮小圖片尺寸。')

    return {
      file: new File([output], webpName(source.name), { type: 'image/webp', lastModified: Date.now() }),
      originalName: source.name || 'clipboard-image.png',
      originalSize: source.size,
      width,
      height,
    }
  } finally {
    bitmap.close()
  }
}
